import React, { useState, useEffect, useCallback } from 'react';

// Get API base URL from environment variable
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const BotResultsTable = ({ selectedBot }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: 'created_date', direction: 'desc' });
  const [pagination, setPagination] = useState({ total: 0, limit: 50, offset: 0, pages: 0 });
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [deleteLoading, setDeleteLoading] = useState(null);
  const [deleteError, setDeleteError] = useState(null);
  const [deleteSuccess, setDeleteSuccess] = useState(null);

  const fetchData = useCallback(async () => {
    if (!selectedBot) {
      setData([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const params = new URLSearchParams({
        sort: sortConfig.key,
        order: sortConfig.direction,
        limit: pagination.limit,
        offset: pagination.offset,
        provider: selectedBot.name // Filter by bot name
      });

      const response = await fetch(`${API_BASE_URL}/api/data/pdf?${params}`, {
        credentials: 'include'
      });

      const result = await response.json();

      if (result.success) {
        setData(result.data);
        setPagination(result.pagination);
        setError(null);
      } else {
        setError(result.message || 'Failed to fetch data');
      }
    } catch (err) {
      setError('Network error. Please try again.');
      console.error('Error fetching bot results:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedBot, sortConfig, pagination.offset, pagination.limit]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
    setPagination(prev => ({ ...prev, offset: 0 }));
  };

  const handlePageChange = (newOffset) => {
    setPagination(prev => ({ ...prev, offset: newOffset }));
  };

  const handleItemsPerPageChange = (newLimit) => {
    setItemsPerPage(newLimit);
    setPagination(prev => ({ ...prev, limit: newLimit, offset: 0 }));
  };

  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) return '↕️';
    return sortConfig.direction === 'asc' ? '↑' : '↓';
  };

  const truncateText = (text, maxLength = 100) => {
    if (!text) return '';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  const getFileExtension = (filename) => {
    if (!filename) return '';
    const ext = filename.split('.').pop().toLowerCase();
    return ext;
  };

  const isLocalFile = (fileName) => {
    if (!fileName) return false;
    // If it's a URL (starts with http/https), it's NOT a local file
    const isUrl = fileName.startsWith('http://') || fileName.startsWith('https://');
    return !isUrl;
  };

  const getCleanFileName = (fileName) => {
    if (!fileName) return '';
    if (!isLocalFile(fileName)) return fileName;
    // Split by both / and \ for cross-platform compatibility
    const parts = fileName.split(/[\\/]/);
    return parts[parts.length - 1];
  };

  const getFileIcon = (filename) => {
    const ext = getFileExtension(filename);
    switch (ext) {
      case 'pdf':
        return '📄';
      case 'doc':
      case 'docx':
        return '📝';
      case 'xls':
      case 'xlsx':
        return '📊';
      case 'ppt':
      case 'pptx':
        return '📋';
      default:
        return '📄';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'N/A';
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'N/A';
    }
  };

  const handleDownload = async (e, id, fileName) => {
    // If it's an external link, let the default <a> tag behavior handle it
    if (!isLocalFile(fileName)) return;
    
    e.preventDefault();
    try {
      // Request a secure download token from the backend
      const response = await fetch(`${API_BASE_URL}/api/files/${id}/download`, {
        credentials: 'include'
      });
      
      const result = await response.json();
      
      if (result.success && result.url) {
        // The backend returns a URL pointing to the file server with a one-time token
        // e.g., http://localhost:8080/download/123?token=xyz
        
        // Trigger the download by creating a temporary link
        const link = document.createElement('a');
        link.href = result.url;
        // Setting download attribute suggests to the browser that it should download rather than navigate
        link.setAttribute('download', result.filename || '');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        console.error('Download error:', result.error);
        alert(result.error || 'Failed to generate download link');
      }
    } catch (err) {
      console.error('Error downloading file:', err);
      alert('Network error. Please try again.');
    }
  };

  const handleDelete = async (id, fileName) => {
    if (!window.confirm(`Are you sure you want to delete "${fileName}"?`)) {
      return;
    }

    try {
      setDeleteLoading(id);
      setDeleteError(null);
      setDeleteSuccess(null);

      const response = await fetch(`${API_BASE_URL}/api/files/${id}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      const result = await response.json();

      if (result.success) {
        setDeleteSuccess(`"${result.data.file_name}" deleted successfully`);
        fetchData();
      } else {
        setDeleteError(result.message || 'Failed to delete record');
      }
    } catch (err) {
      setDeleteError('Network error. Please try again.');
      console.error('Error deleting record:', err);
    } finally {
      setDeleteLoading(null);
    }
  };

  if (!selectedBot) {
    return (
      <div className="table-container">
        <div className="no-data">
          <p>🤖 Please select a bot from the sidebar to view its results</p>
        </div>
      </div>
    );
  }

  if (loading && data.length === 0) {
    return (
      <div className="table-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading bot results...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="table-container">
      <div className="table-header">
        <h2>{selectedBot.name} - Results</h2>
        <p className="table-subtitle">
          Search results collected by {selectedBot.name}
        </p>
      </div>

      {/* Bot Detail Card */}
      <div className="bot-detail-card">
        <div className="bot-detail-header">
          <div className="bot-detail-title">
            <i className="fas fa-robot bot-detail-icon"></i>
            <h3>{selectedBot.name}</h3>
            <span className="bot-status-badge active" title="Active Bot">
              <i className="fas fa-circle"></i> Active
            </span>
          </div>
          <div className="bot-detail-stats">
            <div className="bot-stat-item">
              <i className="fas fa-database"></i>
              <span className="bot-stat-label">Total Results</span>
              <span className="bot-stat-value">{pagination.total || 0}</span>
            </div>
            {data.length > 0 && data[0].created_date && (
              <div className="bot-stat-item">
                <i className="fas fa-clock"></i>
                <span className="bot-stat-label">Last Ran</span>
                <span className="bot-stat-value">{formatDate(data[0].created_date)}</span>
              </div>
            )}
          </div>
        </div>

        <div className="bot-detail-content">
          {selectedBot.properties && selectedBot.properties.length > 0 && (
            <div className="bot-detail-section">
              <span className="bot-detail-label">
                <i className="fas fa-tags"></i> Properties
              </span>
              <div className="bot-tags-container">
                {selectedBot.properties.map((prop, i) => (
                  <span key={i} className="bot-property-tag">
                    {prop}
                  </span>
                ))}
              </div>
            </div>
          )}

          {selectedBot.search_terms && selectedBot.search_terms.length > 0 && (
            <div className="bot-detail-section">
              <span className="bot-detail-label">
                <i className="fas fa-search"></i> Active Searches
              </span>
              <div className="bot-tags-container">
                {selectedBot.search_terms.map((term, i) => (
                  <span key={i} className="bot-search-tag">
                    {term}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Items per page selector */}
      <div className="table-filters">
        <div className="filter-group">
          <label htmlFor="bot-items-per-page">Items per page:</label>
          <select
            id="bot-items-per-page"
            value={itemsPerPage}
            onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
            className="items-per-page-select"
          >
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
          </select>
        </div>
      </div>

      {error && (
        <div className="error-message">
          <p>⚠️ {error}</p>
          <button onClick={fetchData}>Retry</button>
        </div>
      )}
      
      {deleteError && (
        <div className="error-message">
          <p>⚠️ {deleteError}</p>
          <button onClick={() => setDeleteError(null)}>Dismiss</button>
        </div>
      )}
      
      {deleteSuccess && (
        <div className="success-message">
          <p>✅ {deleteSuccess}</p>
          <button onClick={() => setDeleteSuccess(null)}>Dismiss</button>
        </div>
      )}

      {data.length === 0 && !loading ? (
        <div className="no-data">
          <p>📄 No results found for {selectedBot.name}</p>
          <p>Results will appear here once the bot collects data based on your requests.</p>
        </div>
      ) : (
        <>
          {/* Desktop Table View */}
          <div className="table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th onClick={() => handleSort('created_date')} className="sortable">
                    Date {getSortIcon('created_date')}
                  </th>
                  <th>Query</th>
                  <th onClick={() => handleSort('file_name')} className="sortable">
                    File Name {getSortIcon('file_name')}
                  </th>
                  <th onClick={() => handleSort('provider')} className="sortable">
                    Provider {getSortIcon('provider')}
                  </th>
                  <th onClick={() => handleSort('page_number')} className="sortable">
                    Pages {getSortIcon('page_number')}
                  </th>
                  <th>Information</th>
                  <th>Download</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {data.map((item) => (
                  <tr key={item.id}>
                    <td>
                      <div className="file-name-cell">
                        <span className="file-info">📅</span>
                        <span>{formatDate(item.created_date)}</span>
                      </div>
                    </td>
                    <td>
                      <div className="file-name-cell">
                        <span className="file-info">🔍</span>
                        <span>{item.query || 'N/A'}</span>
                      </div>
                    </td>
                    <td className="file-name-cell">
                      <div className="file-info">
                        <span className="file-icon">{getFileIcon(item.file_name)}</span>
                        <div className="file-details">
                          {!isLocalFile(item.file_name) ? (
                            <a 
                              href={item.file_name} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="file-name-link"
                            >
                              {truncateText(item.file_name || item.url, 40)}
                            </a>
                          ) : (
                            <strong>{truncateText(getCleanFileName(item.file_name), 40)}</strong>
                          )}
                          <small className="file-ext">{getFileExtension(item.file_name || item.url).toUpperCase()}</small>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className="provider-tag">{item.provider || 'N/A'}</span>
                    </td>
                    <td className="pages-cell">
                      {item.page_number ? (
                        <span className="page-count">
                          📖 {item.page_number} {item.page_number === 1 ? 'page' : 'pages'}
                        </span>
                      ) : (
                        'N/A'
                      )}
                    </td>
                    <td className="info-cell">
                      {item.info ? (
                        <div className="info-content">
                          {truncateText(item.info, 120)}
                        </div>
                      ) : (
                        <span className="no-info">No additional information</span>
                      )}
                    </td>
                    <td className="download-cell">
                      {item.file_name ? (
                        isLocalFile(item.file_name) ? (
                          <button 
                            className="download-link"
                            onClick={(e) => handleDownload(e, item.id, item.file_name)}
                          >
                            <span className="download-icon">⬇️</span>
                            Download
                          </button>
                        ) : (
                          <a 
                            href={item.file_name} 
                            target="_blank"
                            rel="noopener noreferrer"
                            className="download-link"
                          >
                            <span className="download-icon">⬇️</span>
                            Download
                          </a>
                        )
                      ) : (
                        <span className="no-download">N/A</span>
                      )}
                    </td>
                    <td>
                      <button
                        onClick={() => handleDelete(item.id, item.file_name || item.url)}
                        disabled={deleteLoading === item.id}
                        className="delete-btn"
                        title="Delete record"
                      >
                        {deleteLoading === item.id ? '⏳' : '🗑️'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Card View */}
          <div className="mobile-cards">
            {data.map((item) => (
              <div key={item.id} className="mobile-card">
                <div className="mobile-card-header">
                  <div className="file-info">
                    <span className="file-icon">{getFileIcon(item.file_name)}</span>
                    <div className="file-details">
                      <h3 className="mobile-card-title">
                        {!isLocalFile(item.file_name) ? (
                          <a 
                            href={item.file_name || item.url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="file-name-link"
                          >
                            {truncateText(item.file_name || item.url, 50)}
                          </a>
                        ) : (
                          truncateText(getCleanFileName(item.file_name), 50)
                        )}
                      </h3>
                      <span className="file-ext">{getFileExtension(item.file_name || item.url).toUpperCase()}</span>
                    </div>
                  </div>
                </div>
                
                <div className="mobile-card-content">
                  <div className="mobile-card-field">
                    <span className="mobile-card-label">Provider</span>
                    <span className="mobile-card-value">
                      <span className="provider-tag">{item.provider || 'N/A'}</span>
                    </span>
                  </div>
                  
                  <div className="mobile-card-field">
                    <span className="mobile-card-label">Pages</span>
                    <span className="mobile-card-value">
                      {item.page_number ? (
                        <span className="page-count">
                          📖 {item.page_number} {item.page_number === 1 ? 'page' : 'pages'}
                        </span>
                      ) : (
                        'N/A'
                      )}
                    </span>
                  </div>
                  
                  {item.info && (
                    <div className="mobile-card-field full-width">
                      <span className="mobile-card-label">Information</span>
                      <span className="mobile-card-value">
                        {truncateText(item.info, 120)}
                      </span>
                    </div>
                  )}
                </div>
                
                {(item.file_name || item.url) && (
                  <div className="mobile-card-actions">
                    {isLocalFile(item.file_name || item.url) ? (
                      <button 
                        className="download-link"
                        onClick={(e) => handleDownload(e, item.id, item.file_name || item.url)}
                      >
                        <span className="download-icon">⬇️</span>
                        Download PDF
                      </button>
                    ) : (
                      <a 
                        href={item.file_name || item.url} 
                        target="_blank"
                        rel="noopener noreferrer"
                        className="download-link"
                      >
                        <span className="download-icon">⬇️</span>
                        Download PDF
                      </a>
                    )}
                    <button
                      onClick={() => handleDelete(item.id, item.file_name || item.url)}
                      disabled={deleteLoading === item.id}
                      className="delete-btn mobile-delete-btn"
                      title="Delete record"
                    >
                      {deleteLoading === item.id ? '⏳' : '🗑️'}
                    </button>
                  </div>
                )}
                {!item.url && (
                  <div className="mobile-card-actions">
                    <button
                      onClick={() => handleDelete(item.id, item.file_name || item.url)}
                      disabled={deleteLoading === item.id}
                      className="delete-btn mobile-delete-btn"
                      title="Delete record"
                    >
                      {deleteLoading === item.id ? '⏳' : '🗑️'}
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Pagination */}
          {pagination.pages > 1 && (
            <div className="pagination">
              <div className="pagination-info">
                Showing {pagination.offset + 1}-{Math.min(pagination.offset + pagination.limit, pagination.total)} of {pagination.total} results
              </div>
              <div className="pagination-controls">
                <button
                  onClick={() => handlePageChange(0)}
                  disabled={pagination.offset === 0}
                >
                  First
                </button>
                <button
                  onClick={() => handlePageChange(pagination.offset - pagination.limit)}
                  disabled={pagination.offset === 0}
                >
                  Previous
                </button>
                <span className="page-info">
                  Page {Math.floor(pagination.offset / pagination.limit) + 1} of {pagination.pages}
                </span>
                <button
                  onClick={() => handlePageChange(pagination.offset + pagination.limit)}
                  disabled={pagination.offset + pagination.limit >= pagination.total}
                >
                  Next
                </button>
                <button
                  onClick={() => handlePageChange((pagination.pages - 1) * pagination.limit)}
                  disabled={pagination.offset + pagination.limit >= pagination.total}
                >
                  Last
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {loading && data.length > 0 && (
        <div className="loading-overlay">
          <div className="spinner"></div>
        </div>
      )}
    </div>
  );
};

export default BotResultsTable;
